import { useState, useEffect } from "react";
import CommonLayout from "../../../layouts/CommonLayout";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Spinner, Modal } from "react-bootstrap";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig } from "../../../services/apiUtils";
import "./RecommendedArtist.css";
import SetRecArtistPriorityModel from "../../../components/Model/SetRecArtistPriorityModel";
import { artistImageUrl, extractObjectId } from "../../../utils/utils";
import PageTargetSelector from "../../../components/common/PageTargetSelector";
import usePageTargetSelection from "../../../hooks/usePageTargetSelection";
import {
    FaStar,
    FaFire,
    FaEdit,
    FaTrash,
    FaSortNumericDown,
    FaSearch,
    FaTimes,
    FaUsers,
    FaChevronLeft,
    FaChevronRight,
    FaInfoCircle,
    FaExclamationTriangle
} from "react-icons/fa";

const RecommendedArtist = () => {
    const navigate = useNavigate();
    const pageTarget = usePageTargetSelection();
    const targetParams = pageTarget.selectedTargetParams;
    const selectedTargetLabel = pageTarget.selectedTargetLabel;

    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [recommendedArtists, setRecommendedArtists] = useState([]);
    const [loading, setLoading] = useState(false);
    const [debounceQuery, setDebounceQuery] = useState(searchQuery);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [limit] = useState(20);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [artistToDelete, setArtistToDelete] = useState(null);
    const [showRecArtistPrioritEditModal, setRecArtistPrioritShowEditModal] = useState(false);
    const [recArtistEditData, setRecArtistDetailEditData] = useState({});

    const fetchSearchResults = async (query) => {
        setLoading(true);
        try {
            const response = await allAPiServicesCall.getSearchArtist(
                { query },
                getAuthConfig()
            );
            const searchList = response.data || [];
            setSearchResults(searchList);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            console.error("Error fetching search artist:", error);
            toast.error("Failed to fetch search artists");
        }
    };

    const fetchRecommendedArtists = async (page) => {
        setLoading(true);
        try {
            const params = { limit, page, ...targetParams };

            const response = await allAPiServicesCall.getRecommendedArtists(
                params,
                getAuthConfig()
            );
            const { recommendedList, totalPages, totalRecommendedCount } = response.data;
            setRecommendedArtists(recommendedList);
            setTotalPages(totalPages);
            setTotalCount(totalRecommendedCount || recommendedList?.length || 0);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            console.error("Error fetching recommended artists:", error);
            toast.error("Failed to fetch recommended artists");
        }
    };

    const addArtistToTrends = async (artistId) => {
        setLoading(true);
        try {
            const payload = { artist_id: artistId, ...targetParams };
            const res = await allAPiServicesCall.addTrendingArtist(
                payload,
                getAuthConfig()
            );
            if (res?.success) {
                setSearchQuery("");
                toast.success(`Artist added to trending for ${selectedTargetLabel}`);
                navigate(
                    payload.pageType === "genre" && payload.genreSlug
                        ? `/trending-artist?pageType=genre&genreSlug=${encodeURIComponent(payload.genreSlug)}`
                        : "/trending-artist?pageType=homepage"
                );
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            toast.error("Failed to add to trending");
        }
    };

    const addArtistToRecommended = async (artistId) => {
        setLoading(true);
        try {
            const payload = { artist_id: artistId, ...targetParams };
            await allAPiServicesCall.addRecommendedArtist(
                payload,
                getAuthConfig()
            );
            fetchRecommendedArtists(currentPage);
            toast.success(`Artist added to recommended for ${selectedTargetLabel}`);
            setSearchQuery("");
            setLoading(false);
        } catch (error) {
            setLoading(false);
            toast.error("Failed to add to recommended");
        }
    };

    const handleOnDelete = async () => {
        setLoading(true);
        try {
            const res = await allAPiServicesCall.deleteRecommendedArtist(
                { id: artistToDelete },
                getAuthConfig()
            );
            if (res?.success) {
                fetchRecommendedArtists(currentPage);
                setShowDeleteModal(false);
                toast.success("Artist removed from recommended");
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            toast.error("Failed to remove artist from recommended");
        }
    };

    useEffect(() => {
        const handler = setTimeout(() => setDebounceQuery(searchQuery), 500);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    useEffect(() => {
        if (debounceQuery.trim()) {
            fetchSearchResults(debounceQuery);
        } else {
            fetchRecommendedArtists(currentPage);
        }
    }, [debounceQuery, currentPage, targetParams.pageType, targetParams.genreSlug]);

    const handleSearchChange = (e) => setSearchQuery(e.target.value);

    const confirmDelete = (artistId) => {
        setArtistToDelete(artistId);
        setShowDeleteModal(true);
    };

    const setRecArtistPriority = (artist) => {
        setRecArtistPrioritShowEditModal(true);
        setRecArtistDetailEditData(artist);
    };

    const clearSearch = () => {
        setSearchQuery("");
        setSearchResults([]);
    };

    return (
        <CommonLayout>
            <div className="recommended-artists-page">
                {/* Header Section */}
                <div className="recommended-header">
                    <div className="recommended-header-content">
                        <div className="recommended-header-icon">
                            <FaStar />
                        </div>
                        <div className="recommended-header-text">
                            <h1 className="recommended-title">
                                Recommended Artists
                                {pageTarget.selectedTarget?.pageType === "genre" && (
                                    <span className="recommended-genre-tag">
                                        {pageTarget.selectedTarget?.genreSlug}
                                    </span>
                                )}
                            </h1>
                            <p className="recommended-subtitle">
                                Manage and curate recommended artists for your platform
                            </p>
                        </div>
                    </div>
                    <div className="recommended-stats">
                        <div className="recommended-stat-card">
                            <FaUsers className="recommended-stat-icon" />
                            <div className="recommended-stat-info">
                                <span className="recommended-stat-value">{totalCount}</span>
                                <span className="recommended-stat-label">Total Artists</span>
                            </div>
                        </div>
                    </div>
                </div>

                <PageTargetSelector
                    pageTarget={pageTarget}
                    subtitle="Switch between Homepage and Genre targets before curating recommended artists."
                />

                {/* Search Section */}
                <div className="recommended-search-section">
                    <div className="recommended-search-wrapper">
                        <FaSearch className="recommended-search-icon" />
                        <input
                            type="text"
                            className="recommended-search-input"
                            placeholder="Search for artists to add to trending or recommended..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                        />
                        {searchQuery && (
                            <button className="recommended-search-clear" onClick={clearSearch}>
                                <FaTimes />
                            </button>
                        )}
                    </div>
                    {searchQuery && (
                        <div className="recommended-search-badge">
                            <FaSearch /> Searching for "{searchQuery}"
                        </div>
                    )}
                </div>

                {/* Content Section */}
                <div className="recommended-content">
                    {loading ? (
                        <div className="recommended-loading">
                            <div className="recommended-loading-spinner">
                                <Spinner animation="border" />
                            </div>
                            <p className="recommended-loading-text">Loading artists...</p>
                        </div>
                    ) : searchQuery.trim() ? (
                        // Search Results
                        searchResults.length > 0 ? (
                            <div className="recommended-grid">
                                {searchResults.map((artist) => (
                                    <div key={artist.artist_id} className="recommended-card recommended-card-search">
                                        <div className="recommended-card-image-wrapper">
                                            <img
                                                src={artistImageUrl(artist)}
                                                alt={artist?.artist_name}
                                                className="recommended-card-image"
                                                onClick={() => navigate(`/artist/albums-songs/${artist.artist_id}`)}
                                            />
                                            <div className="recommended-card-overlay">
                                                <div></div>
                                                <div className="recommended-card-badge recommended-card-badge-search">
                                                    <FaSearch /> Search Result
                                                </div>
                                            </div>
                                        </div>
                                        <div className="recommended-card-content">
                                            <h3 className="recommended-card-name">{artist?.artist_name}</h3>
                                            <div className="recommended-card-actions">
                                                <button
                                                    className="recommended-action-btn recommended-action-btn-fire"
                                                    onClick={() => addArtistToTrends(artist.artist_id)}
                                                >
                                                    <FaFire /> Trending
                                                </button>
                                                <button
                                                    className="recommended-action-btn recommended-action-btn-star"
                                                    onClick={() => addArtistToRecommended(artist.artist_id)}
                                                >
                                                    <FaStar /> Recommended
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="recommended-empty">
                                <div className="recommended-empty-icon">
                                    <FaSearch />
                                </div>
                                <h3 className="recommended-empty-title">No Results Found</h3>
                                <p className="recommended-empty-text">
                                    We couldn't find any artists matching "{searchQuery}". Try a different search term.
                                </p>
                                <button className="recommended-empty-btn" onClick={clearSearch}>
                                    <FaTimes /> Clear Search
                                </button>
                            </div>
                        )
                    ) : recommendedArtists.length > 0 ? (
                        // Recommended Artists List
                        <div className="recommended-grid">
                            {recommendedArtists.map((artist, index) => (
                                <div key={artist.artist_id} className="recommended-card">
                                    <div className="recommended-card-image-wrapper">
                                        <img
                                            src={artistImageUrl(artist)}
                                            alt={artist?.artist_name}
                                            className="recommended-card-image"
                                            onClick={() => navigate(`/artist/albums-songs/${artist.artist_id}`)}
                                        />
                                        <div className="recommended-card-overlay">
                                            <div className="recommended-card-rank">
                                                #{(currentPage - 1) * limit + index + 1}
                                            </div>
                                            <div className="recommended-card-badge">
                                                <FaStar /> Recommended
                                            </div>
                                        </div>
                                    </div>
                                    <div className="recommended-card-content">
                                        <h3 className="recommended-card-name">{artist?.artist_name}</h3>
                                        <div className="recommended-card-actions">
                                            <button
                                                className="recommended-action-btn recommended-action-btn-edit"
                                                onClick={() => navigate(`/edit-artist/${artist.artist_id}`)}
                                            >
                                                <FaEdit /> Edit
                                            </button>
                                            <button
                                                className="recommended-action-btn recommended-action-btn-priority"
                                                onClick={() => setRecArtistPriority(artist)}
                                            >
                                                <FaSortNumericDown /> Priority
                                            </button>
                                            <button
                                                className="recommended-action-btn recommended-action-btn-delete"
                                                onClick={() => confirmDelete(extractObjectId(artist?._id))}
                                            >
                                                <FaTrash /> Remove
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="recommended-empty">
                            <div className="recommended-empty-icon">
                                <FaStar />
                            </div>
                            <h3 className="recommended-empty-title">No Recommended Artists</h3>
                            <p className="recommended-empty-text">
                                {`No recommended artists found for ${selectedTargetLabel}. Search above to add artists.`}
                            </p>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {!searchQuery && recommendedArtists.length > 0 && totalPages > 1 && (
                    <div className="recommended-pagination">
                        <button
                            className="recommended-pagination-btn"
                            onClick={() => setCurrentPage((p) => p - 1)}
                            disabled={currentPage === 1}
                        >
                            <FaChevronLeft /> Previous
                        </button>
                        <div className="recommended-pagination-info">
                            <span className="recommended-pagination-current">{currentPage}</span>
                            <span className="recommended-pagination-separator">of</span>
                            <span className="recommended-pagination-total">{totalPages}</span>
                        </div>
                        <button
                            className="recommended-pagination-btn"
                            onClick={() => setCurrentPage((p) => p + 1)}
                            disabled={currentPage === totalPages}
                        >
                            Next <FaChevronRight />
                        </button>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                <Modal
                    show={showDeleteModal}
                    onHide={() => setShowDeleteModal(false)}
                    centered
                    className="recommended-modal"
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <FaExclamationTriangle className="text-danger me-2" />
                            Confirm Removal
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Are you sure you want to remove this artist from the recommended list?
                        This action cannot be undone.
                    </Modal.Body>
                    <Modal.Footer>
                        <button
                            className="recommended-modal-cancel"
                            onClick={() => setShowDeleteModal(false)}
                        >
                            Cancel
                        </button>
                        <button
                            className="recommended-modal-delete"
                            onClick={handleOnDelete}
                        >
                            <FaTrash /> Remove Artist
                        </button>
                    </Modal.Footer>
                </Modal>

                {/* Priority Edit Modal */}
                <SetRecArtistPriorityModel
                    show={showRecArtistPrioritEditModal}
                    handleClose={() => setRecArtistPrioritShowEditModal(false)}
                    artistDetails={recArtistEditData}
                    handleFormSubmit={() => fetchRecommendedArtists(currentPage)}
                />
            </div>
        </CommonLayout>
    );
};

export default RecommendedArtist;
